import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import TopNavigation from './TopNavigation'
import Footer from './Footer'

const PrivacyPolicy = () => {
  const [isSecretRoute, setIsSecretRoute] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const hostname = window.location.hostname
    const pathname = window.location.pathname
    const isSecretSubdomain = hostname.startsWith('secret.') || hostname === 'secret.localhost'
    const isSecretPath = pathname.startsWith('/secret')
    setIsSecretRoute(isSecretSubdomain || isSecretPath)
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-background">
      <TopNavigation backTo="/" backText="Back to Home" />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-foreground mb-8">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">
            <strong>Last updated:</strong> September, 2025
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                Tailtations LLC ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or participate in our SECRET program.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-foreground mb-3">2.1 Personal Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Name and contact information (email address, phone number)</li>
                <li>Billing and shipping addresses</li>
                <li>Payment information (processed securely through third-party providers)</li>
                <li>Account credentials and preferences</li>
                <li>Communication preferences and marketing consent</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">2.2 SECRET Program Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Membership status and participation history</li>
                <li>Event attendance and preferences</li>
                <li>Order history and purchase patterns</li>
                <li>QR code verification data</li>
                <li>Transfer and sharing activities</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">2.3 Technical Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Device information and browser type</li>
                <li>IP address and location data</li>
                <li>Usage analytics and performance data</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Process orders and provide customer service</li>
                <li>Manage your SECRET program membership</li>
                <li>Send order confirmations and shipping updates</li>
                <li>Communicate about events, promotions, and new products</li>
                <li>Improve our website and services</li>
                <li>Prevent fraud and ensure security</li>
                <li>Comply with legal obligations</li>
                <li>Personalize your experience and recommendations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. Information Sharing</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>With service providers who assist in our operations (payment processors, shipping companies, email services)</li>
                <li>When required by law or to protect our rights</li>
                <li>In connection with a business transfer or acquisition</li>
                <li>With your explicit consent</li>
                <li>To prevent fraud or illegal activities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate or incomplete data</li>
                <li>Delete your account and associated data</li>
                <li>Opt-out of marketing communications</li>
                <li>Data portability (receive a copy of your data)</li>
                <li>Withdraw consent for data processing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Cookies and Tracking</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use cookies and similar technologies to enhance your experience, analyze usage patterns, and provide personalized content. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">10. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-foreground font-medium">Tailtations LLC</p>
                <p className="text-muted-foreground">Email: privacy@tailtations.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      {!isSecretRoute && <Footer />}
    </div>
  )
}

export default PrivacyPolicy
